/**
 * useCompanyId Hook
 *
 * Manages companyId from URL query string with localStorage caching
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UseCompanyIdResult {
  companyId: string | null;
  companyIdNumber: number | null;
  hasCompanyId: boolean;
  isValid: boolean;
}

const COMPANY_ID_STORAGE_KEY = 'mcdonate_company_id';

/**
 * Utility function to clear cached companyId from localStorage
 */
export function clearCachedCompanyId(): void {
  try {
    localStorage.removeItem(COMPANY_ID_STORAGE_KEY);
  } catch (error) {
    console.error('[clearCachedCompanyId] Failed to clear localStorage:', error);
  }
}

/**
 * Utility function to manually set companyId in localStorage
 */
export function setCachedCompanyId(companyId: string): void {
  try {
    const companyIdNumber = parseInt(companyId, 10);
    if (!isNaN(companyIdNumber) && companyIdNumber > 0) {
      localStorage.setItem(COMPANY_ID_STORAGE_KEY, companyId);
    }
  } catch (error) {
    console.error('[setCachedCompanyId] Failed to save to localStorage:', error);
  }
}

/**
 * Hook to extract and validate companyId from URL with localStorage fallback
 *
 * Priority:
 * 1. Check URL query parameter (companyId)
 * 2. If found in URL, save to localStorage and use it
 * 3. If not in URL, check localStorage for cached value
 */
export function useCompanyId(): UseCompanyIdResult {
  const [searchParams] = useSearchParams();

  const result = useMemo(() => {
    // First, try to get companyId from URL
    const urlCompanyId = searchParams.get('companyId');

    // If found in URL, use it
    if (urlCompanyId) {
      const companyIdNumber = parseInt(urlCompanyId, 10);
      const isValid = !isNaN(companyIdNumber) && companyIdNumber > 0;

      // Save to localStorage if valid
      if (isValid) {
        try {
          localStorage.setItem(COMPANY_ID_STORAGE_KEY, urlCompanyId);
        } catch (error) {
          console.error('[useCompanyId] Failed to save to localStorage:', error);
        }
      }

      return {
        companyId: urlCompanyId,
        companyIdNumber: isValid ? companyIdNumber : null,
        hasCompanyId: true,
        isValid,
      };
    }

    // If not in URL, try to get from localStorage
    try {
      const cachedCompanyId = localStorage.getItem(COMPANY_ID_STORAGE_KEY);

      if (cachedCompanyId) {
        const companyIdNumber = parseInt(cachedCompanyId, 10);
        const isValid = !isNaN(companyIdNumber) && companyIdNumber > 0;

        return {
          companyId: cachedCompanyId,
          companyIdNumber: isValid ? companyIdNumber : null,
          hasCompanyId: true,
          isValid,
        };
      }
    } catch (error) {
      console.error('[useCompanyId] Failed to read from localStorage:', error);
    }

    // assume 10001 - MALV as default
    localStorage.setItem(COMPANY_ID_STORAGE_KEY, '10001');
    return {
      companyId: '10001',
      companyIdNumber: 10001,
      hasCompanyId: true,
      isValid: true,
    };
  }, [searchParams]);

  return result;
}
